/**
 * IXVerify v2 — the combined attestation screen (ixv2 WP-3.2; plan §3.2 step 4, D13).
 *
 * One signature action sealing up to three recorded claims: kits match, DOB
 * verbally confirmed, and (when the document was photographed) the certified-
 * copy claim (R12). The DOB is unmissable — the largest thing on the screen.
 * Attestation blocks while any full-resolution original is still uploading
 * (R12: the record signs the evidence, not the preview).
 */
import { useState } from 'react';
import { CheckSquare, ChevronLeft, Loader2, PenLine, Square, UploadCloud } from 'lucide-react';
import type { IxvSubject } from '../../lib/api';
import type { AttestPlan } from '../../lib/captureMachine';

function ClaimToggle({
  checked,
  onToggle,
  label,
  testId,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      data-testid={testId}
      className="flex w-full items-start gap-2.5 rounded-xl border border-gray-200 bg-white p-3 text-left transition-colors duration-150 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
    >
      {checked ? (
        <CheckSquare className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
      ) : (
        <Square className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
      )}
      <span className="text-sm text-gray-900 dark:text-gray-100">{label}</span>
    </button>
  );
}

export default function AttestScreen({
  subject,
  plan,
  publishing,
  error,
  onBack,
  onAttest,
}: {
  subject: IxvSubject | null;
  plan: AttestPlan;
  publishing: boolean;
  error: string | null;
  onBack: () => void;
  onAttest: (claims: { kits_confirmed: boolean; dob_confirmed: boolean; copy_certified?: boolean }) => void;
}) {
  const [kitsConfirmed, setKitsConfirmed] = useState(false);
  const [dobConfirmed, setDobConfirmed] = useState(false);
  const [copyCertified, setCopyCertified] = useState(false);

  const claimsComplete = kitsConfirmed && dobConfirmed && (!plan.needsCopyClaim || copyCertified);
  const blocked = plan.originalsPending || !plan.ready;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50 dark:bg-gray-900" data-testid="attest-screen">
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col px-4 py-4 pb-28">
        {/* DOB — the point of the moment, in the largest type on the screen */}
        <section
          aria-label="Verbal date-of-birth check"
          className="rounded-xl border border-primary-200 bg-primary-50 p-5 text-center dark:border-primary-800 dark:bg-primary-900/20"
        >
          <p className="text-xs font-medium uppercase tracking-widest text-primary-700 dark:text-primary-400">
            Ask the patient their date of birth
          </p>
          <p className="mt-2 text-4xl font-bold tabular-nums text-gray-900 dark:text-gray-100" data-testid="attest-dob">
            {subject?.dob || '—'}
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {subject ? (
              <>
                Subject <span className="font-medium tabular-nums">{subject.subject_number}</span>
                {subject.initials ? ` · ${subject.initials}` : ''}
              </>
            ) : (
              'No subject attached'
            )}
          </p>
        </section>

        {/* The claims */}
        <div className="mt-4 space-y-2">
          <ClaimToggle
            checked={kitsConfirmed}
            onToggle={() => setKitsConfirmed((v) => !v)}
            label="The dispensed kit numbers match the IXRS assignment."
            testId="claim-kits"
          />
          <ClaimToggle
            checked={dobConfirmed}
            onToggle={() => setDobConfirmed((v) => !v)}
            label="I verbally confirmed the patient's date of birth."
            testId="claim-dob"
          />
          {plan.needsCopyClaim && (
            <ClaimToggle
              checked={copyCertified}
              onToggle={() => setCopyCertified((v) => !v)}
              label="The captured document is a true and complete copy of the original."
              testId="claim-copy"
            />
          )}
        </div>

        {plan.originalsPending && (
          <p className="mt-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300" role="status" data-testid="originals-pending">
            <UploadCloud className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            The full-resolution original is still uploading — the record signs the evidence, so one moment…
          </p>
        )}
        {error && (
          <p className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {error}
          </p>
        )}
      </div>

      <div
        className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white/95 px-4 py-2 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
      >
        <div className="mx-auto flex w-full max-w-lg items-center justify-between gap-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back
          </button>
          <button
            type="button"
            disabled={!claimsComplete || blocked || publishing}
            onClick={() =>
              onAttest({
                kits_confirmed: kitsConfirmed,
                dob_confirmed: dobConfirmed,
                ...(plan.needsCopyClaim ? { copy_certified: copyCertified } : {}),
              })
            }
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none dark:focus:ring-offset-gray-900"
            data-testid="attest-publish"
          >
            {publishing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> Publishing…
              </>
            ) : (
              <>
                <PenLine className="h-4 w-4" aria-hidden="true" /> Attest &amp; publish
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
